import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardBody, CardTitle, Container, Row, Col } from "reactstrap";

const Header = () => {
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalUsers, setTotalUsers] = useState(null);
  const [previousUsers, setPreviousUsers] = useState(null);
  const [stageList, setStageList] = useState([]);
  const [activeStage, setActiveStage] = useState(null);
  const [previousStage, setPreviousStage] = useState(null);
  const [totalEth, setTotalEth] = useState(0);
  const [totalSol, setTotalSol] = useState(0);
  const [totalBtc, setTotalBtc] = useState(0);
  const [totalHolders, setTotalHolders] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [previousHolders, setPreviousHolders] = useState(0);
  const [previousTransactions, setPreviousTransactions] = useState(0);

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Auth fetch function
  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      throw new Error("Authentication failed");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // Fetch token sale stage data from API
  useEffect(() => {
    const fetchStageData = async () => {
      try {
        const data = await authFetch(`${process.env.REACT_APP_API_URL}/stages`);

        // Handle new tokenomics structure
        if (data.tokenomics && data.tokenomics.length > 0) {
          // Convert tokenomics data to stages format for compatibility
          const stagesData = data.tokenomics.map((tokenomics) => ({
            id: tokenomics._id,
            title: `Stage ${tokenomics.stage}`,
            total_tokens: tokenomics.tokensToSell,
            base_price: tokenomics.priceUSD,
            tokensSold: tokenomics.tokensSold,
            usdCap: tokenomics.usdCap,
            usdRaised: tokenomics.usdRaised,
            isCurrent: tokenomics.isCurrent,
            createdAt: tokenomics.createdAt,
            updatedAt: tokenomics.updatedAt,
            // Add placeholder dates for compatibility
            start_date: new Date(tokenomics.createdAt).toISOString(),
            end_date: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          }));
          setStageList(stagesData);
        }
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };

    fetchStageData();
  }, []);

  useEffect(() => {
    const currentDate = new Date();

    // Find the active stage based on current date or isCurrent field
    const currentActiveStage = stageList.find((stage) => {
      // For new tokenomics structure, use isCurrent field
      if (stage.isCurrent !== undefined) {
        return stage.isCurrent;
      }

      // Fallback to old date-based logic
      const startDate = new Date(stage.start_date);
      const endDate = new Date(stage.end_date);
      return currentDate >= startDate && currentDate <= endDate;
    });

    let previousStage = null;
    if (currentActiveStage) {
      if (currentActiveStage.stage !== undefined) {
        previousStage = stageList.find((stage) => {
          return stage.stage === currentActiveStage.stage - 1;
        });
      } else {
        const currentStageNumber = parseInt(
          currentActiveStage.title.toLowerCase().split(" ")[1],
          10,
        );
        previousStage = stageList.find((stage) => {
          const stageNumber = parseInt(
            stage.title.toLowerCase().split(" ")[1],
            10,
          );
          return stageNumber === currentStageNumber - 1;
        });
      }
    }

    setActiveStage(currentActiveStage);
    setPreviousStage(previousStage);
  }, [stageList]);

  const filterUsersByDateRange = (users, startDate, endDate) => {
    console.log(users);

    return users.filter((user) => {
      const userDate = new Date(user.created_at);
      return userDate >= startDate && userDate <= endDate;
    });
  };

  const getStartOfWeek = (date) => {
    const start = new Date(date);
    const day = start.getDay(),
      diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust for the start of the week (Monday)
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, ordersResponse] = await Promise.all([
          authFetch(`${process.env.REACT_APP_API_URL}/users`),
          authFetch(`${process.env.REACT_APP_API_URL}/orders`),
        ]);

        const currentDate = new Date();
        const currentWeekStart = getStartOfWeek(currentDate);

        const previousWeekStart = new Date(currentWeekStart);
        previousWeekStart.setDate(currentWeekStart.getDate() - 7);
        const previousWeekEnd = new Date(currentWeekStart);
        previousWeekEnd.setDate(currentWeekStart.getDate() - 1);

        const previousWeekUsers = filterUsersByDateRange(
          usersResponse.users,
          previousWeekStart,
          previousWeekEnd,
        );

        setTotalUsers(usersResponse.orders ? usersResponse.orders.length : 0);
        setPreviousUsers(previousWeekUsers.length);

        const totalUsd = ordersResponse.orders.reduce(
          (sum, order) =>
            sum +
            (order.status === "confirmed" && order.isFake === false
              ? parseFloat(order.usdAmount || 0)
              : 0),
          0,
        );
        setTotalPrice(totalUsd);

        const totalEth = ordersResponse.orders.reduce(
          (sum, order) =>
            sum +
            (order.currency.toLowerCase() === "eth" &&
            order.status === "confirmed" &&
            order.isFake === false
              ? parseFloat(order.usdAmount || 0)
              : 0),
          0,
        );
        setTotalEth(totalEth);

        const totalSol = ordersResponse.orders.reduce(
          (sum, order) =>
            sum +
            (order.currency.toLowerCase() === "sol" &&
            order.status === "confirmed" &&
            order.isFake === false
              ? parseFloat(order.usdAmount || 0)
              : 0),
          0,
        );
        setTotalSol(totalSol);

        const totalBtc = ordersResponse.orders.reduce(
          (sum, order) =>
            sum +
            (order.currency.toLowerCase() === "btc" &&
            order.status === "confirmed" &&
            order.isFake === false
              ? parseFloat(order.usdAmount || 0)
              : 0),
          0,
        );
        setTotalBtc(totalBtc);

        // Count the number of distinct wallet addresses (holders)
        const uniqueHolders = new Set(
          ordersResponse.orders
            .filter(
              (order) => order.isFake === false && order.status === "confirmed",
            )
            .map((order) => order.wallet_address),
        );
        setTotalHolders(uniqueHolders.size);

        const uniqueTransactions = new Set(
          ordersResponse.orders.filter(
            (order) => order.isFake === false && order.status === "confirmed",
          ),
        );
        setTotalTransactions(uniqueTransactions.size);

        const previousWeekHolders = new Set(
          ordersResponse.orders
            .filter(
              (order) => order.isFake === false && order.status === "confirmed",
            )
            .filter((order) => {
              const orderDate = new Date(order.createdAt);
              return (
                orderDate >= previousWeekStart && orderDate <= previousWeekEnd
              );
            })
            .map((order) => order.wallet),
        );
        setPreviousHolders(previousWeekHolders.size);

        const previousWeekTransactions = new Set(
          ordersResponse.orders
            .filter(
              (order) => order.isFake === false && order.status === "confirmed",
            )
            .filter((order) => {
              const orderDate = new Date(order.createdAt);
              return (
                orderDate >= previousWeekStart && orderDate <= previousWeekEnd
              );
            }),
        );
        setPreviousTransactions(previousWeekTransactions.size);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="header pb-8 pt-5 pt-md-8" style={{ background: "#251d0c" }}>
      <Container fluid>
        <div className="header-body">
          <Row>
            {/* Token Sale Stage */}
            <Col lg="4" xl="4">
              <Card
                className="card-stats mb-4 mb-xl-0"
                style={{
                  background: "black",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0"
                        style={{ color: "#FFB000" }}
                      >
                        Token Sale Stage{" "}
                        <span style={{ marginLeft: "10px" }}>
                          {activeStage ? activeStage.title : "Loading..."}
                        </span>
                      </CardTitle>

                      <span
                        className="h2 font-weight-bold mb-0"
                        style={{ color: "#FFB000" }}
                      >
                        {activeStage ? activeStage.total_tokens : "Loading..."}
                      </span>
                      <br />
                      <span
                        className="h3 font-weight-bold mb-0"
                        style={{ color: "#FFB000" }}
                      ></span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-yellow text-white rounded-circle shadow">
                        <i className="fas fa-chart-bar" />
                      </div>
                    </Col>
                  </Row>

                  <p
                    className="mt-3 mb-0 text-muted text-sm"
                    style={{ color: "#FFB000" }}
                  >
                    {previousStage ? (
                      <span
                        className={
                          previousStage.total_tokens <
                          (activeStage ? activeStage.total_tokens : 0)
                            ? "text-danger mr-2"
                            : "text-success mr-2"
                        }
                      >
                        <i
                          className={
                            previousStage.total_tokens <
                            (activeStage ? activeStage.total_tokens : 0)
                              ? "fa fa-arrow-down"
                              : "fa fa-arrow-up"
                          }
                        />
                        {previousStage.total_tokens}
                      </span>
                    ) : (
                      "No previous stage"
                    )}
                    <span className="text-nowrap">Since Last Stage</span>
                  </p>
                </CardBody>
              </Card>
            </Col>

            {/* Total Users */}
            <Col lg="4" xl="4">
              <Card
                className="card-stats mb-4 mb-xl-0"
                style={{
                  background: "black",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CardBody>
                  <Row>
                    <div className="col">
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <CardTitle
                            tag="h5"
                            className="text-uppercase text-muted mb-0"
                            style={{ color: "#FFB000" }}
                          >
                            Total Users
                          </CardTitle>
                          <span
                            className="h2 font-weight-bold mb-0"
                            style={{ color: "#FFB000" }}
                          >
                            {totalUsers !== null ? totalUsers : "Loading..."}
                          </span>
                        </div>
                        <div style={{ marginLeft: "10px" }}></div>
                      </div>
                    </div>
                    <Col className="col-auto">
                      <div
                        style={{ width: "45px", height: "45px" }}
                        className="icon icon-shape bg-yellow text-white rounded-circle shadow"
                      >
                        <i
                          style={{ fontSize: "20px" }}
                          className="fas fa-users"
                        />
                      </div>
                    </Col>
                  </Row>
                  <p
                    className="mt-3 mb-0 text-muted text-sm"
                    style={{ color: "#FFB000" }}
                  >
                    {previousUsers !== null && (
                      <span
                        className={
                          previousUsers < totalUsers
                            ? "text-success mr-2"
                            : "text-danger mr-2"
                        }
                      >
                        <i
                          className={
                            previousUsers < totalUsers
                              ? "fa fa-arrow-up"
                              : "fa fa-arrow-down"
                          }
                        />
                        {totalUsers - previousUsers}
                      </span>
                    )}
                    <span className="text-nowrap">Since Last Week</span>
                  </p>
                </CardBody>
              </Card>
            </Col>

            {/* Amount Collected */}
            <Col lg="6" xl="4">
              <Card
                className="card-stats mb-4 mb-xl-0"
                style={{
                  background: "linear-gradient(to right, #FFB000, #F7BE40)", // gradient background from dark yellow to light yellow
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out", // smooth transition for hover
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.05)")
                } // Zoom effect on hover
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                } // Reset zoom when mouse leaves
              >
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase font-weight-bold mb-0"
                        style={{ color: "black" }}
                      >
                        Amount Collected
                      </CardTitle>
                      <span
                        className="h2 font-weight-bold mb-0"
                        style={{ color: "black" }}
                      >
                        {totalPrice.toLocaleString()}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-dark text-white rounded-circle shadow">
                        <h5 style={{ color: "white", fontSize: "30px" }}>$</h5>
                      </div>
                    </Col>
                  </Row>
                  <p
                    className="mt-1 mb-0 font-weight-bold text-sm"
                    style={{ color: "black" }}
                  >
                    <span className="mr-2">USD</span>
                  </p>

                  <Row className="mt-3">
                    <Col>
                      <p
                        className="mb-0 font-weight-bold text-sm"
                        style={{ color: "black" }}
                      >
                        <span className="mr-2">ETH</span>
                        <br />
                        {totalEth.toLocaleString()}
                      </p>
                    </Col>
                    <Col>
                      <p
                        className="mb-0 font-weight-bold text-sm"
                        style={{ color: "black" }}
                      >
                        <span className="mr-2">SOL</span>
                        <br />
                        {totalSol.toLocaleString()}
                      </p>
                    </Col>
                    <Col>
                      <p
                        className="mb-0 font-weight-bold text-sm"
                        style={{ color: "black" }}
                      >
                        <span className="mr-2">BTC</span>
                        <br />
                        {totalBtc.toLocaleString()}
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            {/* Token Sale Stage */}
            <Col lg="4" xl="4">
              <Card
                className="card-stats mb-4 mb-xl-0"
                style={{
                  background: "black",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CardBody>
                  <Row>
                    <div className="col">
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0"
                        style={{ color: "#FFB000" }}
                      >
                        Total Token Holders
                      </CardTitle>

                      <span
                        className="h2 font-weight-bold mb-0"
                        style={{ color: "#FFB000" }}
                      >
                        {totalHolders !== null ? totalHolders : "Loading..."}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-yellow text-white rounded-circle shadow">
                        <i className="fas fa-id-card" />
                      </div>
                    </Col>
                  </Row>

                  <p
                    className="mt-3 mb-0 text-muted text-sm"
                    style={{ color: "#FFB000" }}
                  >
                    {previousHolders !== null && (
                      <span
                        className={
                          previousHolders < totalHolders
                            ? "text-success mr-2"
                            : "text-danger mr-2"
                        }
                      >
                        <i
                          className={
                            previousHolders < totalHolders
                              ? "fa fa-arrow-up"
                              : "fa fa-arrow-down"
                          }
                        />
                        {totalHolders - previousHolders}
                      </span>
                    )}
                    <span className="text-nowrap">Since Last Week</span>
                  </p>
                </CardBody>
              </Card>
            </Col>

            {/* Total Users */}
            <Col lg="4" xl="4">
              <Card
                className="card-stats mb-4 mb-xl-0"
                style={{
                  background: "black",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CardBody>
                  <Row>
                    <div className="col">
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <CardTitle
                            tag="h5"
                            className="text-uppercase text-muted mb-0"
                            style={{ color: "#FFB000" }}
                          >
                            Total Transactions
                          </CardTitle>
                          <span
                            className="h2 font-weight-bold mb-0"
                            style={{ color: "#FFB000" }}
                          >
                            {totalTransactions !== null
                              ? totalTransactions
                              : "Loading..."}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Col className="col-auto">
                      <div
                        style={{ width: "45px", height: "45px" }}
                        className="icon icon-shape bg-yellow text-white rounded-circle shadow"
                      >
                        <i
                          style={{ fontSize: "20px" }}
                          className="fas  fa-wallet"
                        />
                      </div>
                    </Col>
                  </Row>
                  <p
                    className="mt-3 mb-0 text-muted text-sm"
                    style={{ color: "#FFB000" }}
                  >
                    {previousTransactions !== null && (
                      <span
                        className={
                          previousTransactions < totalTransactions
                            ? "text-success mr-2"
                            : "text-danger mr-2"
                        }
                      >
                        <i
                          className={
                            previousTransactions < totalTransactions
                              ? "fa fa-arrow-up"
                              : "fa fa-arrow-down"
                          }
                        />
                        {totalTransactions - previousTransactions}
                      </span>
                    )}
                    <span className="text-nowrap">Since Last Week</span>
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
        {/* Stages Data */}
      </Container>
    </div>
  );
};

export default Header;
